/**
 * Contact Support (web) — /support
 *
 * The web app previously had NO way to raise a support request. `/help` is a
 * crisis-helpline directory (Samaritans, food banks, welfare) — genuinely
 * useful, but it cannot take a message, and it was the thing labelled "Help &
 * Support" in the footer. So the Terms of Service promised customers they
 * could withdraw marketing consent "by sending a support request via the
 * website or app", and on the website that was impossible.
 *
 * Two things this page has to get right that the mobile HelpButton does not:
 *
 *  1. GUESTS. 163 of the marketing-consented numbers on file belong to
 *     guest-checkout customers with no account. Under PECR an opt-out must be
 *     easy and free — so this form works signed out, collecting a name and an
 *     email or phone instead of a user id. It never dead-ends into a login wall.
 *
 *  2. CATEGORY. "Unsubscribe from marketing messages" is the first option and
 *     is stored in a real column, so opt-outs are filterable in AdminSupport
 *     rather than buried in free text.
 *
 * `?topic=unsubscribe` pre-selects the opt-out category, so the Terms, the
 * Privacy Policy and any future "unsubscribe" link can point at one URL that
 * lands the customer on a pre-filled form.
 */
import { ImageAttachmentPicker, useImageAttachments } from "@/components/MessageImages";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "wouter";
import {
  LifeBuoy, Send, CheckCircle2, ChevronLeft, Mail, Heart, Loader2, BellOff,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useSEO } from "@/hooks/use-seo";
import { cn } from "@/lib/utils";
import {
  RADII, TYPE, ELEVATION, FOCUS_RING, TAP_TARGET, InlineAlert, Chip,
} from "@/components/hub";

const SUPPORT_EMAIL = "hello@ayrshirehub.co.uk";

/**
 * Mirrors SUPPORT_CATEGORIES in api-server/src/routes/support.ts. Kept as a
 * literal rather than fetched so the form renders instantly and still works if
 * the categories endpoint is unreachable — the server re-validates the id
 * regardless, so drift fails closed rather than storing junk.
 */
const CATEGORIES = [
  { id: "unsubscribe", label: "Unsubscribe from marketing messages", hint: "Stop texts and promo messages" },
  { id: "order",       label: "Problem with an order",               hint: "Missing, wrong or late order" },
  { id: "delivery",    label: "Delivery or driver issue",            hint: "Driver, address or delivery code" },
  { id: "payment",     label: "Payment or refund",                   hint: "Charges, refunds and receipts" },
  { id: "account",     label: "Account or login",                    hint: "Can't sign in, change details" },
  { id: "business",    label: "My business, shop or listing",        hint: "Listings, menus and the portal" },
  { id: "report",      label: "Report content or a user",            hint: "Something that shouldn't be here" },
  { id: "other",       label: "Something else",                      hint: "Anything not covered above" },
] as const;

type CategoryId = (typeof CATEGORIES)[number]["id"];

const PLACEHOLDER: Record<CategoryId, string> = {
  unsubscribe: "e.g. Please stop sending me marketing texts on 07700 900123.",
  order:       "Which shop, roughly when you ordered, and what went wrong.",
  delivery:    "What happened with the delivery, and the address or delivery code if you have it.",
  payment:     "What you were charged, when, and what you expected.",
  account:     "What you're trying to do and what the app or site says instead.",
  business:    "Your business name and what needs changing.",
  report:      "What you're reporting and where you saw it.",
  other:       "Tell us what you need help with.",
};

/* ── Field primitives ─────────────────────────────────────────────────
   Local to this page: the hub design system has no form controls yet, so
   these wrap the tokens rather than hand-rolling classes per input.     */

const FIELD_BASE = cn(
  RADII.md, TYPE.body, FOCUS_RING,
  "w-full border px-3.5 py-2.5 transition-colors",
  "bg-white text-slate-800 border-slate-200 placeholder:text-slate-400",
  "dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700 dark:placeholder:text-slate-500",
);

function Field({
  id, label, hint, required, children,
}: {
  id: string;
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className={cn(TYPE.bodyStrong, "text-slate-700 dark:text-slate-200")}>
        {label}
        {required
          ? <span className="text-red-500 ml-0.5" aria-hidden>*</span>
          : <span className={cn(TYPE.footnote, "text-slate-400 dark:text-slate-500 ml-1.5 font-normal")}>optional</span>}
      </label>
      {children}
      {hint && (
        <p id={`${id}-hint`} className={cn(TYPE.footnote, "text-slate-500 dark:text-slate-400")}>{hint}</p>
      )}
    </div>
  );
}

export default function Support({ defaultCategory }: { defaultCategory?: CategoryId }) {
  const attachments = useImageAttachments("support");
  const { user } = useAuth();

  useSEO({
    title: "Contact Support",
    description:
      "Get help from the Ayrshire Hub team, or unsubscribe from marketing messages. No account needed — you can contact us without signing in.",
  });

  // ?topic=unsubscribe (or ?category=…) pre-selects a category, as does the
  // /unsubscribe route via `defaultCategory`. Read once — this page never
  // pushes to the URL, so the two cannot drift apart.
  const initialCategory = useMemo<CategoryId>(() => {
    const params = new URLSearchParams(window.location.search);
    const wanted = (params.get("topic") ?? params.get("category") ?? "").toLowerCase();
    if (CATEGORIES.some((c) => c.id === wanted)) return wanted as CategoryId;
    return defaultCategory ?? "order";
  }, [defaultCategory]);

  const [category, setCategory] = useState<CategoryId>(initialCategory);
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [honeypot, setHoneypot] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const errorRef = useRef<HTMLDivElement>(null);
  const successRef = useRef<HTMLDivElement>(null);

  const isGuest = !user;
  const isUnsubscribe = category === "unsubscribe";

  // Move focus to whichever status region just appeared, so a screen-reader or
  // keyboard user is not left at the bottom of a form that has been replaced.
  useEffect(() => { if (sent) successRef.current?.focus(); }, [sent]);
  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);

  const messageTooShort = message.trim().length > 0 && message.trim().length < 5;
  const canSubmit =
    message.trim().length >= 5 &&
    !submitting && !attachments.uploading &&
    (!isGuest || (name.trim().length >= 2 && (email.trim().length > 0 || phone.trim().length > 0)));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if(submitting || attachments.uploading)return;
    setError(null);

    if (message.trim().length < 5) {
      setError("Please tell us a little more about what you need help with.");
      return;
    }
    if (isGuest) {
      if (name.trim().length < 2) { setError("Please tell us your name."); return; }
      if (!email.trim() && !phone.trim()) {
        setError(isUnsubscribe
          ? "To stop marketing messages we need the number or email they're sent to."
          : "Please give us an email address or phone number so we can reply.");
        return;
      }
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("ayrshire_token");
      const res = await fetch("/api/support-requests", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: message.trim(),
          attachments: attachments.claims,
          category,
          // Sent only when signed out; the server ignores them otherwise.
          ...(isGuest
            ? { name: name.trim(), email: email.trim(), phone: phone.trim(), website: honeypot }
            : {}),
        }),
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(
          body?.error ??
            (res.status === 429
              ? "You've sent several requests recently. Please wait a little while, or email us."
              : "Something went wrong sending your request. Please try again."),
        );
      }

      setSent(true);
      attachments.clear();
      setMessage("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Success ──────────────────────────────────────────────────────── */
  if (sent) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div
          ref={successRef}
          tabIndex={-1}
          role="status"
          aria-live="polite"
          className={cn(
            RADII.lg, ELEVATION.md, FOCUS_RING,
            "bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 p-7 text-center",
          )}
        >
          <div className="w-16 h-16 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 grid place-items-center mx-auto mb-4">
            <CheckCircle2 size={32} className="text-emerald-600" aria-hidden />
          </div>
          <h1 className={cn(TYPE.title1, "text-slate-900 dark:text-slate-50 mb-2")}>Request sent</h1>
          <p className={cn(TYPE.body, "text-slate-600 dark:text-slate-300 mb-1")}>
            {isUnsubscribe
              ? "We've logged your request to stop marketing messages. We'll action it and confirm."
              : "A member of the Ayrshire Hub team will get back to you."}
          </p>
          <p className={cn(TYPE.subhead, "text-slate-500 dark:text-slate-400")}>
            {user
              ? "We'll reply in your Messages."
              : email.trim()
                ? `We'll reply to ${email.trim()}.`
                : "We'll be in touch on the number you gave us."}
          </p>

          <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
            <Link
              href="/"
              className={cn(
                RADII.md, TYPE.bodyStrong, FOCUS_RING, TAP_TARGET,
                "inline-flex items-center px-5 py-2.5 bg-[#1e3a5f] text-white hover:bg-[#1e3a5f]/90 transition-colors",
              )}
            >
              Back to Ayrshire Hub
            </Link>
            <button
              type="button"
              onClick={() => { setSent(false); setError(null); }}
              className={cn(
                RADII.md, TYPE.bodyStrong, FOCUS_RING, TAP_TARGET,
                "inline-flex items-center px-5 py-2.5 border border-slate-200 dark:border-slate-700",
                "text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors",
              )}
            >
              Send another
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Form ─────────────────────────────────────────────────────────── */
  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <button
        type="button"
        onClick={() => window.history.back()}
        className={cn(
          TYPE.callout, FOCUS_RING, RADII.sm,
          "inline-flex items-center gap-1 text-slate-500 hover:text-[#1e3a5f] dark:text-slate-400 dark:hover:text-slate-200 transition-colors mb-5 -ml-1",
        )}
      >
        <ChevronLeft size={18} aria-hidden /> Back
      </button>

      {/* Header */}
      <div className="flex items-start gap-3.5 mb-2">
        <div className={cn(RADII.md, "w-11 h-11 bg-[#e8eef5] dark:bg-slate-800 grid place-items-center shrink-0")}>
          <LifeBuoy size={22} className="text-[#1e3a5f] dark:text-slate-200" aria-hidden />
        </div>
        <div>
          <h1 className={cn(TYPE.display, "text-slate-900 dark:text-slate-50")}>Contact support</h1>
          <p className={cn(TYPE.body, "text-slate-500 dark:text-slate-400 mt-1")}>
            Tell us what's up and the Ayrshire Hub team will get back to you.
            {isGuest && " You don't need an account."}
          </p>
        </div>
      </div>

      {/* Crisis cross-link. /help is a different thing and must stay findable —
          someone in crisis should not have to read a contact form to find it. */}
      <Link
        href="/help"
        className={cn(
          RADII.md, FOCUS_RING,
          "flex items-center gap-2.5 mt-5 px-3.5 py-2.5 border transition-colors",
          "border-rose-200 bg-rose-50 hover:bg-rose-100",
          "dark:border-rose-900/50 dark:bg-rose-900/20 dark:hover:bg-rose-900/30",
        )}
      >
        <Heart size={16} className="text-rose-600 shrink-0" aria-hidden />
        <span className={cn(TYPE.callout, "text-rose-800 dark:text-rose-200")}>
          <strong className="font-bold">Need urgent help?</strong>{" "}
          Mental health, food banks, benefits and crisis helplines are on our Local Support page.
        </span>
      </Link>

      <form onSubmit={(e) => void handleSubmit(e)} className="mt-6 flex flex-col gap-6" noValidate>

        {/* ── Category ── */}
        <fieldset className="flex flex-col gap-2.5 min-w-0">
          <legend className={cn(TYPE.bodyStrong, "text-slate-700 dark:text-slate-200 mb-1.5")}>
            What's it about?
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CATEGORIES.map((c) => {
              const active = category === c.id;
              return (
                <label
                  key={c.id}
                  className={cn(
                    RADII.md, TAP_TARGET,
                    "flex items-start gap-2.5 px-3.5 py-2.5 border cursor-pointer transition-colors",
                    "focus-within:outline-none focus-within:ring-2 focus-within:ring-[#1e3a5f] focus-within:ring-offset-2",
                    active
                      ? "border-[#1e3a5f] bg-[#e8eef5] dark:bg-slate-800 dark:border-slate-300"
                      : "border-slate-200 bg-white hover:border-slate-300 dark:bg-slate-900 dark:border-slate-700 dark:hover:border-slate-600",
                  )}
                >
                  <input
                    type="radio"
                    name="category"
                    value={c.id}
                    checked={active}
                    onChange={() => setCategory(c.id)}
                    className="mt-1 accent-[#1e3a5f] w-4 h-4 shrink-0"
                  />
                  <span className="min-w-0">
                    <span className={cn(
                      TYPE.callout, "block font-semibold",
                      active ? "text-[#1e3a5f] dark:text-slate-100" : "text-slate-700 dark:text-slate-200",
                    )}>
                      {c.label}
                    </span>
                    <span className={cn(TYPE.footnote, "block text-slate-500 dark:text-slate-400 mt-0.5")}>
                      {c.hint}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        {isUnsubscribe && (
          <InlineAlert tone="info" title="Stopping marketing messages">
            Give us the mobile number or email address the messages arrive on and we'll
            remove it. This is free, and we'll never charge or make you sign in for it.
            Service messages about an order you've placed are separate and will still be sent.
          </InlineAlert>
        )}

        {/* ── Message ── */}
        <Field
          id="support-message"
          label="How can we help?"
          required
          hint={`${message.trim().length}/2000 characters`}
        >
          <textarea
            id="support-message"
            name="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={6}
            maxLength={2000}
            required
            aria-describedby="support-message-hint"
            aria-invalid={messageTooShort || undefined}
            placeholder={PLACEHOLDER[category]}
            className={cn(FIELD_BASE, "min-h-[140px] resize-y")}
          />
        </Field>

        {/* ── Guest contact details ── */}
        {isGuest ? (
          <div className={cn(
            RADII.md,
            "border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 p-4 flex flex-col gap-4",
          )}>
            <div>
              <p className={cn(TYPE.bodyStrong, "text-slate-700 dark:text-slate-200")}>How should we reply?</p>
              <p className={cn(TYPE.footnote, "text-slate-500 dark:text-slate-400 mt-0.5")}>
                You're not signed in, so we need a way to reach you. An email address or a
                phone number is enough.{" "}
                <Link href="/login" className="text-[#1e3a5f] dark:text-slate-200 underline">
                  Sign in
                </Link>{" "}
                instead if you have an account and we'll reply in your Messages.
              </p>
            </div>

            <Field id="support-name" label="Your name" required>
              <input
                id="support-name"
                name="name"
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                required
                className={FIELD_BASE}
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field
                id="support-email"
                label="Email address"
                hint={isUnsubscribe ? "If the messages come by email" : "We'll reply here"}
              >
                <input
                  id="support-email"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  maxLength={200}
                  aria-describedby="support-email-hint"
                  className={FIELD_BASE}
                />
              </Field>

              <Field
                id="support-phone"
                label="Mobile number"
                hint={isUnsubscribe ? "The number the texts arrive on" : "If you'd rather we text or call"}
              >
                <input
                  id="support-phone"
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  maxLength={32}
                  aria-describedby="support-phone-hint"
                  className={FIELD_BASE}
                />
              </Field>
            </div>

            {/* Honeypot. Hidden from sight and from assistive tech; bots fill it. */}
            <div aria-hidden className="absolute w-px h-px -left-[9999px] overflow-hidden">
              <label htmlFor="support-website">Website</label>
              <input
                id="support-website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
            </div>
          </div>
        ) : (
          <p className={cn(
            RADII.md, TYPE.callout,
            "border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-4 py-3 text-slate-600 dark:text-slate-300",
          )}>
            Signed in as <strong className="font-semibold">{user.name}</strong> — we'll reply in your{" "}
            <Link href="/messages" className="text-[#1e3a5f] dark:text-slate-100 underline">Messages</Link>.
          </p>
        )}

        {error && (
          <div ref={errorRef} tabIndex={-1} role="alert" aria-live="assertive" className="outline-none">
            <InlineAlert tone="danger" title="We couldn't send that">
              {error}{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="underline font-semibold">
                Email {SUPPORT_EMAIL}
              </a>{" "}
              if this keeps happening.
            </InlineAlert>
          </div>
        )}

        {/* ── Submit ── */}
        <div className="flex flex-col gap-3">
          <ImageAttachmentPicker state={attachments} disabled={submitting}/>
          <button
            type="submit"
            disabled={!canSubmit}
            className={cn(
              RADII.md, TYPE.bodyStrong, FOCUS_RING, TAP_TARGET, ELEVATION.sm,
              "inline-flex items-center justify-center gap-2 px-6 py-3 w-full sm:w-auto sm:self-start",
              "bg-[#1e3a5f] text-white transition-colors",
              "hover:bg-[#1e3a5f]/90 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#1e3a5f]",
            )}
          >
            {submitting
              ? <><Loader2 size={16} className="animate-spin" aria-hidden /> Sending…</>
              : isUnsubscribe
                ? <><BellOff size={16} aria-hidden /> Send opt-out request</>
                : <><Send size={16} aria-hidden /> Send request</>}
          </button>

          <p className={cn(TYPE.footnote, "text-slate-500 dark:text-slate-400")}>
            Prefer email? Write to{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
                isUnsubscribe ? "Unsubscribe from marketing messages" : "Ayrshire Hub support",
              )}`}
              className={cn(FOCUS_RING, RADII.xs, "text-[#1e3a5f] dark:text-slate-200 underline font-semibold")}
            >
              {SUPPORT_EMAIL}
            </a>
            . We aim to reply within two working days.
          </p>
        </div>
      </form>

      {/* Footer cross-links */}
      <div className="mt-10 pt-6 border-t border-slate-100 dark:border-slate-800 flex flex-wrap items-center gap-2">
        <Chip tone="danger" icon={<Heart size={11} aria-hidden />}>
          <Link href="/help" className={cn(FOCUS_RING, RADII.xs)}>Local Support &amp; crisis helplines</Link>
        </Chip>
        <Chip tone="neutral" icon={<Mail size={11} aria-hidden />}>
          <a href={`mailto:${SUPPORT_EMAIL}`} className={cn(FOCUS_RING, RADII.xs)}>{SUPPORT_EMAIL}</a>
        </Chip>
        <Chip tone="neutral">
          <Link href="/privacy" className={cn(FOCUS_RING, RADII.xs)}>Privacy Policy</Link>
        </Chip>
      </div>
    </div>
  );
}
